/* eslint-disable react/no-array-index-key */
import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import Autocomplete from 'src/components/Autocomplete/Autocomplete';
import MoneyMask from 'src/components/MoneyMask/MoneyMask';
import { formatAmountWithDecimals } from 'src/utils/formatAmout';
import FieldLabel from './FieldLabel';

const columnLabelSx = {
  display: 'block',
  mb: 0.75,
  color: '#68717c',
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: '0.04em',
  textTransform: 'uppercase'
};

const CfdiAmountRowList = ({
  rows,
  availableCfdis,
  isLoadingCfdis,
  errors,
  touched,
  getOptionLabel,
  onChange,
  cfdiLabel,
  totalLabel
}) => {
  const { t } = useTranslation();

  const handleCfdiChange = (index, value) => {
    const updated = [...rows];
    updated[index] = {
      cfdi: value,
      amount: value ? value.amountIvaIncluded : 0
    };
    onChange(updated);
  };

  const handleAmountChange = (index, amount) => {
    const updated = [...rows];
    updated[index] = { ...updated[index], amount };
    onChange(updated);
  };

  const handleRemoveRow = index => {
    onChange(rows.filter((_, i) => i !== index));
  };

  const handleAddRow = () => {
    onChange([...rows, { cfdi: null, amount: 0 }]);
  };

  const totalAmount = rows.reduce((acc, row) => acc + (row.amount || 0), 0);

  const selectedCfdiIds = new Set(
    rows.map(row => row.cfdi?.id).filter(Boolean)
  );

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
        <Typography sx={{ ...columnLabelSx, width: '60%' }}>
          {cfdiLabel}
        </Typography>
        <Typography sx={{ ...columnLabelSx, width: '35%' }}>
          {t('COMMON.AMOUNT_WITH_IVA')}
        </Typography>
      </Box>
      {rows.map((row, index) => {
        const rowError = touched && errors?.[index];
        return (
          <Box key={index} sx={{ mt: index === 0 ? 0 : 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Autocomplete
                options={availableCfdis.filter(
                  option =>
                    !selectedCfdiIds.has(option.id) ||
                    option.id === row.cfdi?.id
                )}
                id={`cfdi-row-${index}`}
                value={row.cfdi}
                loading={isLoadingCfdis}
                disabled={isLoadingCfdis}
                loadingText={t('COMMON.LOADING')}
                getOptionLabel={getOptionLabel}
                onChange={(_e, value) => handleCfdiChange(index, value)}
                error={Boolean(rowError)}
                sx={{ width: '60%' }}
              />
              <TextField
                sx={{ width: '35%' }}
                InputProps={{
                  autoComplete: 'off',
                  inputComponent: MoneyMask
                }}
                error={Boolean(rowError)}
                disabled={!row.cfdi}
                name={`cfdi-amount-${index}`}
                onChange={e => handleAmountChange(index, e.target.value)}
                value={row.amount}
                variant="outlined"
              />
              {rows.length > 1 && (
                <IconButton
                  aria-label={t('COMMON.DELETE')}
                  onClick={() => handleRemoveRow(index)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            {rowError && (
              <Typography variant="caption" color="secondary.main">
                {t(rowError)}
              </Typography>
            )}
          </Box>
        );
      })}

      <Button
        type="button"
        color="primary"
        variant="text"
        disabled={isLoadingCfdis || selectedCfdiIds.size >= availableCfdis.length}
        onClick={handleAddRow}
        startIcon={<AddIcon />}
        sx={{ mt: 1, alignSelf: 'flex-start' }}
      >
        {t('COMMON.ADD')}
      </Button>

      <FieldLabel label={totalLabel} disabled>
        <TextField
          fullWidth
          disabled
          InputProps={{
            readOnly: true,
            inputComponent: MoneyMask
          }}
          value={formatAmountWithDecimals(totalAmount, 2)}
        />
      </FieldLabel>
    </>
  );
};

CfdiAmountRowList.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      cfdi: PropTypes.object,
      amount: PropTypes.number
    })
  ).isRequired,
  availableCfdis: PropTypes.array.isRequired,
  isLoadingCfdis: PropTypes.bool.isRequired,
  errors: PropTypes.array,
  touched: PropTypes.bool,
  getOptionLabel: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  cfdiLabel: PropTypes.string.isRequired,
  totalLabel: PropTypes.string.isRequired
};

CfdiAmountRowList.defaultProps = {
  errors: [],
  touched: false
};

export default CfdiAmountRowList;
